import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import Sites from '@toasttab/shared/src/data/Sites.json'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as CloseIcon } from '@toasttab/shared/src/assets/svg/icon-close.svg'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import Link from '@toasttab/shared/src/components/atoms/Link/Link'

export interface MainRegionSelectorProps {
  currentLanguage?: string
  footerLinkCss: any
  localizedEntries?: any[]
  packageName: string
  siteId: number
  hostName?: string
}

const getLinkLanguage = (language: string) => {
  switch (language) {
    case 'en-US':
      return 'en'
    case 'en-GB':
      return 'uk'
    case null:
      return null
    default:
      return language.toLowerCase().split('-')[1]
  }
}

const MainRegionSelector = ({
  currentLanguage,
  footerLinkCss,
  localizedEntries,
  packageName,
  siteId,
  hostName
}: MainRegionSelectorProps) => {
  const { languages, originalPath, localized, ready, prefix } = useI18next()
  const [showRegionModal, setRegionModal] = useState(false)
  const [currentPath, setCurrentPath] = useState('')

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentPath(window.location.pathname.replace(/\/ca|ie|uk\//, ''))
    }
  }, [])

  const modalClasses = classNames(
    'fixed bg-black bg-opacity-30 transition-opacity inset-0 left-0 top-0 w-full z-50 flex min-h-full items-center justify-center p-4 text-center',
    {
      hidden: !showRegionModal,
      flex: showRegionModal
    }
  )

  const getCurrentSiteName = () => {
    const site = Sites.sites.find((x) => x.siteId === siteId)
    return site.siteName
  }

  return (
    <>
      <div>
        <button
          aria-label='Open region selector'
          data-modal-target='regionSelect-modal'
          data-modal-toggle='regionSelect-modal'
          data-testid='MainRegionSelector-openButton'
          className={`${footerLinkCss} before:content-icon-map-marker before:mr-2 before:relative before:top-1`}
          onClick={() => setRegionModal(!showRegionModal)}
        >
          Region: {getCurrentSiteName()}
        </button>
      </div>
      <div
        aria-hidden={showRegionModal}
        aria-modal={showRegionModal}
        className={modalClasses}
        data-testid='MainRegionSelector-modal'
        id='regionSelect-modal'
        onClick={() => setRegionModal(false)}
        role={showRegionModal ? 'none' : 'modal'}
      >
        <div className='relative bg-white rounded-lg shadow-xl max-w-[550px] max-h-[800px] w-full overflow-y-auto'>
          <button
            aria-label='Close region selector'
            className='absolute top-1 right-1'
            data-testid='MainRegionSelector-closeButton'
            onClick={() => setRegionModal(false)}
          >
            <CloseIcon width='30' height='30' />
          </button>
          <nav aria-label='Switch language' role='navigation' className='p-10'>
            <h4
              className='relative text-default font-effra text-2xl font-medium mb-6'
              id='modal-title'
            >
              Select your region
            </h4>
            <ul>
              <>
                {Sites.sites
                  .filter((site) => site.defaultRegion)
                  .map((site) => {
                    const entry = localizedEntries?.find(
                      (x) => x.language === site.language
                    )
                    let url = ''
                    if (entry?.slug || entry?.slugOverride) {
                      if (entry.slugOverride) {
                        url = entry.slugOverride.startsWith('/')
                          ? entry.slugOverride
                          : '/' + entry.slugOverride
                      } else {
                        url = entry.slug.startsWith('/')
                          ? entry.slug
                          : '/' + entry.slug
                      }
                    }
                    if (entry?.language) {
                      const site = Sites.sites
                        .filter((site) => site.defaultRegion)
                        .find((x) => x.language === entry.language)
                      return (
                        <li
                          key={site.language}
                          data-testid='MainRegionSelector-listItem-localizedEntries'
                        >
                          <Link
                            to={url}
                            language={getLinkLanguage(entry.language)}
                            hrefLang={entry.language.toLowerCase()}
                            className='text-gray-100 font-sansPro font-normal visited:text-gray-100'
                          >
                            {site.siteName}
                          </Link>
                        </li>
                      )
                    } else {
                      return (
                        <li
                          key={site.language}
                          data-testid='MainRegionSelector-listItem-region'
                        >
                          <Link
                            to={`${
                              site.url === ''
                                ? hostName
                                : hostName +
                                  '/' +
                                  getLinkLanguage(site.language)
                            }`}
                            language={getLinkLanguage(site.language)}
                            hrefLang={site.language.toLowerCase()}
                            className='text-gray-100 font-sansPro font-normal visited:text-gray-100 hover:text-default'
                          >
                            {site.siteName}
                          </Link>
                        </li>
                      )
                    }
                  })}
              </>
            </ul>
          </nav>
        </div>
      </div>
    </>
  )
}

export default MainRegionSelector
